<template>
  <div v-if="person && person.userUUID" class="person" :class="[options.classes, isCurrentUser(person) ? 'me' : '']">
    <div class="person-container">
      <v-popover
        v-if="!options.hidePopover"
        popoverClass="person-popover"
        :autoHide="true"
        offset="5"
        placement="auto-end"
        :popperOptions="popperOptions"
        :container="options.popoverContainer"
      >
        <div
          class="avatar"
          :class="[online ? 'user_in' : 'user_out', bannedClass]"
          :style="{ 'background-image': backgroundImage }"
        >
          <div v-if="!photo" class="initials">{{ initials }}</div>
          <div class="presence" :class="online ? 'user_in' : 'user_out'"></div>
          <div v-if="isInConnections(person.userUUID, userConnectionsStatuses.ACTIVE)" class="network">
            <i class="ar-icon icon-check-circle-light" aria-hidden="true"></i>
          </div>
        </div>
        <person-popover
          slot="popover"
          :person="person"
          :isBanned="isBanned"
          :tableType="tableType"
          :options="options"
          @banUser="banUserEvent()"
        ></person-popover>
      </v-popover>
      <div
        v-else
        class="avatar"
        :class="[online ? 'user_in' : 'user_out', bannedClass]"
        :style="{ 'background-image': backgroundImage }"
      >
        <div v-if="!photo" class="initials">{{ initials }}</div>
        <div class="presence" :class="online ? 'user_in' : 'user_out'"></div>
        <div v-if="isInConnections(person.userUUID, userConnectionsStatuses.ACTIVE)" class="network">
          <i class="ar-icon icon-check-circle-light" aria-hidden="true"></i>
        </div>
      </div>

      <template v-if="!options.avatarOnly">
        <div class="guest-name">
          <span class="name">{{ getFullName(options.showIsMe) }}</span>
          <div v-if="!options.hideJob" class="company-container">
            <span v-if="jobtitle" class="title">{{ jobtitle }}</span>
            <span v-if="company" class="company"> {{ company }}</span>
          </div>
          <span v-if="online && !isSingleRoom" class="room-name" :style="{ color: personRoomColor }">
            {{ personRoomName }}
          </span>
        </div>

        <template v-if="!options.hideAdminIcon && context.SHOW_ADMIN_ICONS">
          <div v-if="person.isEventAdmin || isDomainAdmin(person.emailDomain)" class="ico-admin">
            admin
            <i class="ar-icon icon-user-light" aria-hidden="true"></i>
          </div>
        </template>

        <div v-if="options.inlineActions && !isCurrentUser(person)" class="actions">
          <div v-if="currentUserPermissions.canNetwork && options.networkRestore" class="action">
            <a
              v-if="isInConnections(person.userUUID, userConnectionsStatuses.ACTIVE)"
              @click="removeFromNetwork(person)"
              href="javascript:void(0);"
              class="user_in"
            >
              <div class="ico"><i class="clickable ar-icon icon-times-circle-light" aria-hidden="true"></i></div>
            </a>
            <a v-else @click="restoreNetwork(person)" href="javascript:void(0);" class="user_in">
              <div class="ico"><i class="clickable ar-icon icon-check-circle-light" aria-hidden="true"></i></div>
            </a>
          </div>

          <div v-if="currentUserPermissions.canNetwork && permissions.canPrivateChat" class="action">
            <a
              @click="sendPrivateMessage(person)"
              href="javascript:void(0);"
              :class="person.isOnline ? 'user_in' : 'user_out'"
            >
              <div class="ico"><i class="clickable ar-icon icon-comment-alt-light"></i></div>
            </a>
          </div>

          <div v-if="currentUserPermissions.canNetwork && permissions.canPrivateChat" class="action">
            <template v-if="videoChatFunctionalityIsActive && canUserSendVideoCallInvites(person)">
              <a
                @click="createVideoCallInvite(person.userUUID)"
                href="javascript:void(0);"
                :title="title"
                :class="{
                  user_in: person.mediaDeviceStatus === mediaDeviceStatuses.ALLOW,
                  user_busy: person.mediaDeviceStatus === mediaDeviceStatuses.BUSY
                }"
              >
                <div class="ico"><i class="ar-icon icon-video-light"></i></div>
              </a>
            </template>
            <template v-else>
              <div class="ico user_out" title="Not available"><i class="ar-icon icon-video-light"></i></div>
            </template>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import PersonMixin from "@/components/DigitalEvent/RoomList/Room/mixins/PersonMixin";
import PersonPopover from "@/components/DigitalEvent/RoomList/Room/People/PersonPopover";
import PersonActionsMixin from "@/components/DigitalEvent/RoomList/Room/mixins/PersonActionsMixin";
import { MEDIA_DEVICE_STATUSES } from "@/helpers/constants";
import VideoCallInviteMixin from "@/components/DigitalEvent/RoomList/Room/mixins/Invite/VideoCallInviteMixin";
import { mapGetters, mapActions, mapMutations, mapState } from "vuex";

export default {
  name: "Person",
  mixins: [PersonMixin, PersonActionsMixin, VideoCallInviteMixin],
  components: {
    personPopover: PersonPopover
  },
  props: {
    person: {
      type: Object,
      required: true,
      validator: function(value) {
        return value && value.profile;
      }
    },
    tableType: {
      type: String,
      required: false
    },
    options: {
      type: Object,
      required: false,
      default: () => ({
        actions: true,
        showIsMe: false,
        avatarOnly: false,
        hideJob: false,
        popoverContainer: "body",
        hideAdminIcon: false
      })
    }
  },
  data() {
    return {
      popperOptions: {
        modifiers: {
          preventOverflow: {
            escapeWithReference: true
          }
        }
      },
      isBanned: false
    };
  },
  computed: {
    ...mapGetters({
      getUser: "users/getUser",
      isDomainAdmin: "users/isDomainAdmin",
      currentUserPermissions: "users/currentUserPermissions",
      activeRoom: "room/activeRoom",
      rooms: "room/rooms",
      isSingleRoom: "room/isSingleRoom"
    }),
    ...mapState("event", ["context"]),
    title() {
      return this.person.mediaDeviceStatus === this.mediaDeviceStatuses.ALLOW
        ? "Available to chat"
        : this.person.mediaDeviceStatus === this.mediaDeviceStatuses.BUSY
        ? "Busy on a call"
        : "Not available";
    },
    /**
     * Media device statuses
     * @return {Object}
     */
    mediaDeviceStatuses() {
      return MEDIA_DEVICE_STATUSES;
    },
    bannedClass() {
      return this.person.isBanned && this.isAdmin ? "banned" : "";
    },
    isAdmin() {
      return this.user.is_admin || this.isDomainAdmin(this.getUser(this.user.user_uuid).emailDomain);
    },
    isPersonBanned() {
      return this.person.isBanned;
    },
    personRoom() {
      return this.rooms.find(room => room.uuid === this.person.roomUUID);
    },
    personRoomName() {
      return this.personRoom.uuid === this.activeRoom.uuid
        ? `${this.personRoom.TITLE} (current)`
        : this.personRoom.TITLE;
    },
    personRoomColor() {
      return this.personRoom.ROOM_COLOUR_TYPE === "custom" ? this.personRoom.ROOM_COLOUR : "var(--color-primary)";
    }
  },
  methods: {
    ...mapActions("users", ["banUser"]),
    ...mapMutations("room", ["updateStateUserInfo"]),
    async banUserEvent() {
      this.isBanned = !this.isBanned;
      this.person.isBanned = this.isBanned;
      this.updateStateUserInfo({
        userUUID: this.person.userUUID,
        data: this.person
      });
      await this.banUser({
        isBanned: this.isBanned,
        userUUID: this.person.userUUID
      });
    }
  },
  watch: {
    //Synchronise button state for admin users if some user was banned
    isPersonBanned: function(val) {
      this.isBanned = val;
    }
  },
  created() {
    this.isBanned = this.person.isBanned;
  }
};
</script>
